import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { XIcon, CheckIcon } from 'lucide-react';
import { CartItemType, ProductOption } from '../types/types';
import { PIZZA_OPTIONS } from '../data/menuData';
import { useLanguage } from '../context/LanguageContext';
interface PizzaBuilderProps {
  onClose: () => void;
  onSave: (item: CartItemType) => void;
  initialItem?: CartItemType;
}
export function PizzaBuilder({
  onClose,
  onSave,
  initialItem
}: PizzaBuilderProps) {
  const {
    t,
    formatPrice
  } = useLanguage();
  const [selectedSize, setSelectedSize] = useState<{
    name: string;
    slices?: string;
    price: number;
  }>(initialItem?.selections?.selectedSize || {
    name: PIZZA_OPTIONS.sizes[0].name,
    slices: PIZZA_OPTIONS.sizes[0].slices,
    price: PIZZA_OPTIONS.sizes[0].price
  });
  const [selectedSauce, setSelectedSauce] = useState<ProductOption>(initialItem?.selections?.pizzaSauce || PIZZA_OPTIONS.sauces[0]);
  const [selectedToppings, setSelectedToppings] = useState<ProductOption[]>(initialItem?.selections?.pizzaToppings || []);
  const [comments, setComments] = useState(initialItem?.selections?.comentarios || '');
  const handleToppingToggle = (topping: ProductOption) => {
    const isSelected = selectedToppings.some(t => t.id === topping.id);
    if (isSelected) {
      setSelectedToppings(selectedToppings.filter(t => t.id !== topping.id));
    } else {
      setSelectedToppings([...selectedToppings, topping]);
    }
  };
  const calculateSubtotal = () => {
    let total = selectedSize.price;
    total += selectedSauce.price;
    selectedToppings.forEach(topping => {
      total += topping.price;
    });
    return total;
  };
  const handleSave = () => {
    const item: CartItemType = {
      id: initialItem?.id || `custom_pizza_${Date.now()}`,
      productId: 'custom_pizza',
      name: 'Pizza Personalizada',
      basePrice: selectedSize.price,
      quantity: initialItem?.quantity || 1,
      selections: {
        selectedSize,
        pizzaSauce: selectedSauce,
        pizzaToppings: selectedToppings,
        comentarios: comments
      },
      subtotal: calculateSubtotal(),
      type: 'pizza'
    };
    onSave(item);
  };
  const subtotalFormatted = formatPrice(calculateSubtotal());
  return <motion.div initial={{
    opacity: 0,
    y: 50
  }} animate={{
    opacity: 1,
    y: 0
  }} exit={{
    opacity: 0,
    y: 50
  }} className="fixed inset-0 z-50 bg-carbon flex flex-col max-w-md mx-auto shadow-2xl" data-id="element-1476">
      {/* Header */}
      <div className="bg-surface px-4 py-4 flex items-center justify-between shadow-sm z-20 sticky top-0 border-b border-gray-800" data-id="element-1477">
        <button onClick={onClose} className="p-2 -ml-2 text-gray-400 hover:text-white bg-surface/80 rounded-full backdrop-blur-sm" data-id="element-1478">
          <XIcon className="w-6 h-6" data-id="element-1479" />
        </button>
        <h2 className="font-heading font-semibold text-lg text-white tracking-wide" data-id="element-1480">
          Armá tu Pizza
        </h2>
        <div className="w-10" data-id="element-1481"></div>
      </div>

      <div className="flex-1 overflow-y-auto pb-24" data-id="element-1482">
        {/* Hero Image */}
        <div className="w-full h-48 relative bg-surface-light" data-id="element-1483">
          <img src="https://images.unsplash.com/photo-1513104890138-7c749659a591?w=600&h=400&fit=crop" alt="Pizza Personalizada" className="w-full h-full object-cover" loading="eager" data-id="element-1484" />
          <div className="absolute inset-0 bg-gradient-to-t from-carbon via-carbon/40 to-transparent" data-id="element-1485" />
          <div className="absolute bottom-4 left-4 right-4 text-white" data-id="element-1486">
            <h3 className="text-3xl font-heading font-bold mb-1 drop-shadow-md tracking-wide" data-id="element-1487">
              Pizza a tu antojo
            </h3>
            <p className="text-gray-300 text-sm drop-shadow" data-id="element-1488">
              Elegí el tamaño, la salsa y todos los ingredientes que querrás.
            </p>
          </div>
        </div>

        <div className="p-4" data-id="element-1489">
          {/* Size Selection */}
          <div className="mb-8" data-id="element-1490">
            <h4 className="font-heading font-semibold text-lg text-white tracking-wide mb-3" data-id="element-1491">
              1. Elegí el tamaño
            </h4>
            <div className="space-y-2" data-id="element-1492">
              {PIZZA_OPTIONS.sizes.map(size => <button key={size.id} onClick={() => setSelectedSize({
              name: size.name,
              slices: size.slices,
              price: size.price
            })} className={`w-full flex items-center justify-between p-4 rounded-xl border transition-all ${selectedSize.name === size.name ? 'border-verde bg-verde/10 ring-1 ring-verde' : 'border-gray-700 bg-surface hover:border-gray-500'}`} data-id="element-1493">
                  <div className="text-left" data-id="element-1494">
                    <p className="font-medium text-white" data-id="element-1495">{size.name}</p>
                    <p className="text-xs text-gray-400" data-id="element-1496">{size.slices}</p>
                  </div>
                  <p className="font-semibold text-verde" data-id="element-1497">
                    {formatPrice(size.price).primary}
                  </p>
                </button>)}
            </div>
          </div>

          {/* Sauce Selection */}
          <div className="mb-8" data-id="element-1498">
            <h4 className="font-heading font-semibold text-lg text-white tracking-wide mb-3" data-id="element-1499">
              2. Elegí la salsa base
            </h4>
            <div className="grid grid-cols-1 gap-2" data-id="element-1500">
              {PIZZA_OPTIONS.sauces.map(sauce => <button key={sauce.id} onClick={() => setSelectedSauce(sauce)} className={`p-3 rounded-xl border text-sm text-left transition-colors flex items-center justify-between ${selectedSauce.id === sauce.id ? 'border-verde bg-verde/10 text-verde font-medium ring-1 ring-verde' : 'border-gray-700 bg-surface text-gray-300 hover:border-gray-500'}`} data-id="element-1501">
                  <span data-id="element-1502">{sauce.name}</span>
                  {selectedSauce.id === sauce.id && <CheckIcon className="w-4 h-4" data-id="element-1503" />}
                </button>)}
            </div>
          </div>

          {/* Toppings Selection */}
          <div className="mb-8" data-id="element-1504">
            <div className="mb-4" data-id="element-1505">
              <h4 className="font-heading font-semibold text-lg text-white tracking-wide" data-id="element-1506">
                3. Agregá tus ingredientes
              </h4>
              <p className="text-gray-500 text-xs" data-id="element-1507">
                Seleccioná todos los que querrás.
              </p>
            </div>

            {PIZZA_OPTIONS.toppingCategories.map(category => <div key={category.id} className="mb-6" data-id="element-1508">
                <h5 className="font-medium text-gray-300 mb-2" data-id="element-1509">
                  {category.name}
                </h5>
                <div className="grid grid-cols-2 gap-2" data-id="element-1510">
                  {category.items.map(topping => {
                const isSelected = selectedToppings.some(t => t.id === topping.id);
                return <button key={topping.id} onClick={() => handleToppingToggle(topping)} className={`p-2.5 rounded-xl border text-sm text-left transition-colors flex flex-col justify-between min-h-[72px] ${isSelected ? 'border-verde bg-verde/10 ring-1 ring-verde' : 'border-gray-700 bg-surface hover:border-gray-500'}`} data-id="element-1511">
                        <span className={`font-medium leading-tight mb-1 ${isSelected ? 'text-verde' : 'text-gray-200'}`} data-id="element-1512">
                          {topping.name}
                        </span>
                        <span className={`text-xs font-semibold ${isSelected ? 'text-verde/80' : 'text-gray-400'}`} data-id="element-1513">
                          +{formatPrice(topping.price).primary}
                        </span>
                      </button>;
              })}
                </div>
              </div>)}
          </div>

          {/* Comments Section */}
          <div className="mb-8" data-id="element-1514">
            <div className="flex items-center gap-2 mb-1" data-id="element-1515">
              <h4 className="font-heading font-semibold text-lg text-white tracking-wide" data-id="element-1516">
                {t('comments')}
              </h4>
              <span className="text-xs font-medium bg-surface-light text-gray-400 px-2 py-0.5 rounded-full border border-gray-700" data-id="element-1517">
                {t('optional')}
              </span>
            </div>
            <p className="text-gray-500 text-xs mb-3" data-id="element-1518">
              {t('specialInstructions')}
            </p>
            <textarea value={comments} onChange={e => setComments(e.target.value)} placeholder={t('commentsPlaceholder')} className="w-full h-24 p-4 bg-surface-light border border-gray-700 rounded-xl focus:ring-2 focus:ring-verde outline-none resize-none text-white placeholder-gray-500" data-id="element-1519" />
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="bg-surface border-t border-gray-800 p-4 pb-safe sticky bottom-0 z-20" data-id="element-1520">
        <button onClick={handleSave} className="w-full py-4 rounded-xl font-semibold flex items-center justify-center gap-2 transition-colors min-h-[48px] border border-white/10 bg-terracota text-white active:bg-[#9a3a18] shadow-lg shadow-terracota/20" data-id="element-1521">
          {t('addToOrder')} — {subtotalFormatted.primary}
        </button>
      </div>
    </motion.div>;
}